import { createFileRoute, Link } from '@tanstack/react-router'
import { ArrowLeft } from 'lucide-react'
import { useState, type FormEvent } from 'react'
import { Field, FormAlert } from '@/components/app/form'
import { CaptchaSubmit, useCaptcha } from '@/components/app/turnstile'
import { authClient, authErrorMessage } from '@/lib/auth-client'

export const Route = createFileRoute('/_auth/forgot-password')({
  head: () => ({ meta: [{ title: 'Reset password · Nebueye' }] }),
  component: ForgotPassword,
})

function ForgotPassword() {
  const [pending, setPending] = useState(false)
  const [error, setError] = useState<string>()
  const [sent, setSent] = useState(false)
  const captcha = useCaptcha('password-reset')

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setPending(true)
    setError(undefined)
    const { error } = await authClient.requestPasswordReset({
      email: String(new FormData(e.currentTarget).get('email')),
      redirectTo: '/reset-password',
      fetchOptions: { headers: captcha.headers },
    })
    captcha.reset()
    setPending(false)
    // Same answer whether or not the account exists; only our own refusals show.
    if (error && (error.status === 429 || error.status === 400 || error.status === 403)) return setError(authErrorMessage(error))
    setSent(true)
  }

  return (
    <div className="space-y-7">
      <Link to="/sign-in" className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5 text-sm">
        <ArrowLeft className="size-4" /> Back to sign in
      </Link>
      <div className="space-y-2">
        <h1 className="text-headline font-semibold">Reset your password</h1>
        <p className="text-muted-foreground text-[15px]">We'll email you a link to choose a new one.</p>
      </div>
      {sent ? (
        <FormAlert tone="success">If an account uses that email, a reset link is on its way. It expires in 1 hour.</FormAlert>
      ) : (
        <form onSubmit={onSubmit} className="space-y-4">
          {error && <FormAlert>{error}</FormAlert>}
          <Field label="Email" name="email" type="email" autoComplete="email" required autoFocus />
          {captcha.widget}
          <CaptchaSubmit captcha={captcha} pending={pending}>
            Send reset link
          </CaptchaSubmit>
        </form>
      )}
    </div>
  )
}
